import React from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import SectionMessage from '@atlaskit/section-message';
import { useIntl, defineMessages } from 'react-intl';

import { type Extension } from '../web-client';
import { createAtlAttributionHeader } from '../utils';
import { type ForgeModalDeprecationMessage_appQuery as appQuery } from './__generated__/ForgeModalDeprecationMessage_appQuery.graphql';
import { RelaySuspensefulBoundary } from '../utils/RelaySuspensefulBoundary';

interface ForgeModalDeprecationMessageProps {
	extension?: Extension;
}

/**
 * This message is displayed when a forge custom field is using a deprecated modal experience for rendering on the Jira issue view.
 * It is displayed in a modal dialog and is not dismissible.
 * It is one of the steps related to the deprecation of the modal experience which was decided here:
 * https://hello.atlassian.net/wiki/spaces/Ditto/pages/5092641172/DACI+-+Modal+experience+for+the+Forge+custom+fields+on+issue+view+clean-up?focusedCommentId=5111022370
 */
const ForgeModalDeprecationMessageInner = (props: ForgeModalDeprecationMessageProps) => {
	const { extension } = props;

	const data = useLazyLoadQuery<appQuery>(
		graphql`
			query ForgeModalDeprecationMessage_appQuery($id: ID!) {
				app(id: $id) {
					name
				}
			}
		`,
		{
			id: extension?.id || '',
		},
		{
			networkCacheConfig: {
				metadata: {
					headers: {
						'atl-attribution': createAtlAttributionHeader(),
					},
				},
			},
		},
	);

	const intl = useIntl();

	return (
		<SectionMessage title={intl.formatMessage(messages.title)} appearance="error">
			{intl.formatMessage(messages.message, { appName: data.app?.name })}
		</SectionMessage>
	);
};

const ForgeModalDeprecationMessageFallback = (props: ForgeModalDeprecationMessageProps) => {
	const intl = useIntl();
	const appName = props.extension?.properties.title;
	return (
		<SectionMessage title={intl.formatMessage(messages.title)} appearance="error">
			{intl.formatMessage(messages.message, { appName })}
		</SectionMessage>
	);
};

export const ForgeModalDeprecationMessage = (
	props: ForgeModalDeprecationMessageProps,
): React.JSX.Element => {
	const fallback = <ForgeModalDeprecationMessageFallback {...props} />;
	return (
		<RelaySuspensefulBoundary fallback={fallback} errorFallback={fallback}>
			<ForgeModalDeprecationMessageInner {...props} />
		</RelaySuspensefulBoundary>
	);
};

const messages = defineMessages({
	title: {
		id: 'forge.modal.deprecation.title',
		defaultMessage: 'Update your app',
		description: 'A section title that informs the user that the app needs to be updated.',
	},
	message: {
		id: 'forge.modal.deprecation.message',
		defaultMessage:
			'Your app {appName} uses the deprecated modal editing experience for custom fields, which is being phased out. Please have your system admin update the app to use inline editing instead. If issues remain, contact the app developer.',
		description:
			'A section message that informs the user that the app needs to be updated as it is using a deprecated platform feature and will soon stop working.',
	},
});
